import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
    awaitSheetAnswer, closeSignInSheet, dismissSignInSheet, getSignInSheet, markSheetSent, subscribeSignInSheet,
} from '../services/signInSheet';
import {
    RESEND_AFTER_MS, codeTrouble, emailSenderIsOurs, finishWithCode, isHomeScreenApp, looksLikeEmail,
    sendEmailLink, sendTroubleCopy,
} from '../services/emailLinkSignIn';
import { postSignInDestination, rememberDestination } from '../utils/postSignIn';
import { moreThanGoogle, providerName } from '../services/signInProviders';
import { answerLink } from '../services/signInLink';
import ProviderButtons from './ProviderButtons';
import { useToast } from './Toast';

// The sign-in sheet (#3928), founder pick A of the sign-in mocks
// (2026-10-11): Google stays the one big button; email is a quiet link under
// it; inside an app's browser, where Google refuses sign-in, the email field
// leads. On iPhone and iPad Safari with phone_email_first on, the email field
// leads too, Google a full button under it (#5084, founder pick A,
// 2026-10-21). No password field anywhere. Mounted once at the app root;
// opened through services/signInSheet.js. With more_sign_in_options on
// (#5154, founder pick A, 2026-10-22) the provider buttons are Apple, Google
// and Facebook, all the same size, in the platform's order
// (signInProviders.js); the sheet draws the list the open decided and never
// branches on a provider's name. With nothing beyond Google it is today's
// sheet exactly.

const GREEN = '#1A4D2E';

// The Home Screen app's code mail goes out through our own Gmail sender
// (email_service.send_via_gmail_smtp, #4189), not Firebase's noreply@.
export const CODE_SENDER = 'stratiaadmissions@gmail.com';

export function senderLine(code = false) {
    if (code) return `Check spam or Promotions too. It comes from ${CODE_SENDER}.`;
    return emailSenderIsOurs() ? 'Check spam or Promotions too. It comes from noreply@stratiaadmissions.com.' : 'Check spam or Promotions too.';
}

function useToastIfAny() {
    try { return useToast(); } catch { return null; }
}

/** The code box (#4189, founder pick A): under "We sent a sign-in link",
 *  only in the iPhone Home Screen app. A one-time-code field, so iOS offers
 *  the code from Mail above the keyboard; the sixth digit signs in. */
export function CodeBox({ onDead }) {
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [problem, setProblem] = useState(null);
    const navigate = useNavigate();
    const toast = useToastIfAny();
    const tried = useRef('');

    const submit = async (value = code) => {
        const digits = String(value).replace(/\D/g, '');
        if (busy) return;
        if (digits.length !== 6) { setProblem(codeTrouble('wrong_code').copy); return; }
        tried.current = digits;
        setBusy(true);
        setProblem(null);
        const res = await finishWithCode(digits);
        setBusy(false);
        if (res.user) {
            closeSignInSheet();
            toast?.success?.("You're signed in on this iPhone app.");
            try { navigate(await postSignInDestination(res.user.email), { replace: true }); } catch { navigate('/universities', { replace: true }); }
            return;
        }
        const trouble = codeTrouble(res.reason);
        if (trouble.dead && onDead) { onDead(trouble.copy); return; }
        setProblem(trouble.copy);
    };

    const onChange = (e) => {
        const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
        setCode(digits);
        if (problem) setProblem(null);
        if (digits.length === 6 && digits !== tried.current) submit(digits);
    };

    return (
        <form onSubmit={(e) => { e.preventDefault(); submit(); }} noValidate data-testid="signin-code-form">
            <p className="mb-2 text-sm text-[#4A544C]">In this app, type the 6-digit code from that email. The link would open Safari instead.</p>
            <label htmlFor="signin-code" className="mb-1 block text-xs font-medium text-[#4A544C]">Code from the email</label>
            <input
                id="signin-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={onChange}
                aria-invalid={problem ? 'true' : undefined}
                aria-describedby={problem ? 'signin-code-problem' : undefined}
                data-testid="signin-code-input"
                className="mb-2 block w-full rounded-xl border-[1.5px] border-[#CFCBBE] bg-white px-3 py-2 font-mono text-[22px] tracking-[0.32em] text-[#1F2A22] focus:border-[#1A4D2E] focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/30"
            />
            {problem && <p id="signin-code-problem" role="alert" className="mb-2 text-sm text-red-700">{problem}</p>}
            <button type="submit" disabled={busy} data-testid="signin-code-submit"
                className="flex min-h-[44px] w-full items-center justify-center rounded-full px-5 text-[15px] font-semibold text-white disabled:opacity-60"
                style={{ background: GREEN }}>
                {busy ? 'Signing in...' : 'Sign in'}
            </button>
        </form>
    );
}

/** The email field and its send button, shared with the finish page. */
export function EmailLinkForm({ entry, flow, onSent, buttonLabel = 'Email me a sign-in link', autoFocus = false, initialEmail = '', dest = null,
    beforeSend = null, describedBy = null }) {
    const [email, setEmail] = useState(initialEmail);
    const [busy, setBusy] = useState(false);
    const [problem, setProblem] = useState(null);
    const inputRef = useRef(null);

    useEffect(() => { if (autoFocus) inputRef.current?.focus(); }, [autoFocus]);

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        if (!looksLikeEmail(email)) { setProblem(sendTroubleCopy('auth/invalid-email')); return; }
        setBusy(true);
        setProblem(null);
        // An app's-browser sheet opened before the answer on email (#5084):
        // false means email is off, and the sheet has closed for the notice.
        if (beforeSend && !(await beforeSend())) { setBusy(false); return; }
        // Where the link lands, kept now that one is really sent: it rides
        // the tab's carry, which the send copies (#4117).
        if (dest) rememberDestination(dest);
        const res = await sendEmailLink(email, { entry, flow });
        setBusy(false);
        if (res.ok) onSent?.(email.trim()); else setProblem(sendTroubleCopy(res.code));
    };

    return (
        <form onSubmit={submit} noValidate>
            <label htmlFor="signin-email" className="mb-1 block text-xs font-medium text-[#4A544C]">Email</label>
            <input
                id="signin-email"
                ref={inputRef}
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={problem ? 'true' : undefined}
                aria-describedby={[problem ? 'signin-email-problem' : null, describedBy].filter(Boolean).join(' ') || undefined}
                placeholder="you@example.com"
                className="mb-2 block w-full rounded-xl border-[1.5px] border-[#CFCBBE] bg-white px-3 py-2.5 text-[15px] text-[#1F2A22] focus:border-[#1A4D2E] focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/30"
            />
            {problem && (
                <p id="signin-email-problem" role="alert" className="mb-2 text-sm text-red-700">{problem}</p>
            )}
            <button
                type="submit"
                disabled={busy}
                data-testid="signin-email-send"
                className="flex min-h-[44px] w-full items-center justify-center rounded-full px-5 text-[15px] font-semibold text-white transition-colors disabled:opacity-60"
                style={{ background: GREEN }}
            >
                {busy ? 'Sending...' : buttonLabel}
            </button>
        </form>
    );
}

/** "Check your email": what was sent, where, and the two ways on. */
export function CheckYourEmail({ email, entry, flow, inApp, onDifferent, onCodeDead }) {
    // Decided once: the box belongs to the Home Screen app only.
    const [codeMode] = useState(() => isHomeScreenApp());
    const [sentAt, setSentAt] = useState(() => Date.now());
    const [now, setNow] = useState(() => Date.now());
    const [note, setNote] = useState(null);
    const [busy, setBusy] = useState(false);
    const wait = Math.max(0, Math.ceil((sentAt + RESEND_AFTER_MS - now) / 1000));

    useEffect(() => {
        if (wait <= 0) return undefined;
        const t = setTimeout(() => setNow(Date.now()), 1000);
        return () => clearTimeout(t);
    }, [wait, now]);

    const again = async () => {
        if (wait > 0 || busy) return;
        setBusy(true);
        setNote(null);
        const res = await sendEmailLink(email, { entry, flow, resend: true });
        setBusy(false);
        if (res.ok) { setSentAt(Date.now()); setNow(Date.now()); setNote('Sent again.'); } else setNote(sendTroubleCopy(res.code));
    };

    return (
        <div data-testid="signin-check-email">
            <div role="status" className="mb-3 rounded-xl border border-[#C7DACB] bg-[#E6F0E8] px-3 py-2.5 text-sm text-[#22342A]">
                We sent a sign-in link to <span className="ph-mask break-all font-medium">{email}</span>. It works once.
            </div>
            {codeMode ? <CodeBox onDead={onCodeDead} /> : (
                <p className="mb-2 text-sm text-[#4A544C]">
                    {inApp
                        ? "Tap the link in your Mail app. It opens in your phone's browser and signs you in there."
                        : 'Tap the link in that email to finish signing in.'}
                </p>
            )}
            <div className="flex flex-col items-start">
                <button type="button" onClick={again} disabled={wait > 0 || busy}
                    className="min-h-[44px] text-sm font-semibold underline underline-offset-4 disabled:text-[#7C857E] disabled:no-underline"
                    style={wait > 0 || busy ? undefined : { color: GREEN }}>
                    {wait > 0 ? `Didn't get it? You can send it again in ${wait}s` : "Didn't get it? Send again"}
                </button>
                <button type="button" onClick={onDifferent}
                    className="min-h-[44px] text-sm font-semibold underline underline-offset-4" style={{ color: GREEN }}>
                    Use a different email
                </button>
            </div>
            {note && <p className="mt-1 text-sm text-[#4A544C]" aria-live="polite">{note}</p>}
            <p className="mt-2 text-xs text-[#5D6A60]">{senderLine(codeMode)}</p>
        </div>
    );
}

// Variant A's "or" between the email and Google (#5084). Hidden from screen
// readers: the order already reads email, then Google.
function OrRule() {
    return (
        <div className="my-3 flex items-center gap-3 text-xs text-[#5D6A60]" aria-hidden="true" data-testid="signin-sheet-or">
            <span className="h-px flex-1 bg-[#E2DED3]" />or<span className="h-px flex-1 bg-[#E2DED3]" />
        </div>
    );
}

// The amber line on the account-exists and refusal sheets (#5154).
function SheetAlert({ children }) {
    return (
        <p role="alert" data-testid="signin-sheet-alert"
            className="mb-3 rounded-xl border border-[#EBCF9F] bg-[#FBF3E2] px-3 py-2.5 text-[13px] text-[#7A5410]">
            {children}
        </p>
    );
}

// "Also sign in with Apple next time?" (#5154). Nothing is linked without
// the yes (Apple requires explicit consent before an Apple account is linked
// to other data); a close is a no.
function LinkConsent({ link, onDone }) {
    const [busy, setBusy] = useState(false);
    const toast = useToastIfAny();
    const name = providerName(link.pending.provider);
    const answer = async (a) => {
        if (busy) return;
        setBusy(true);
        const outcome = await answerLink(link.user, link.pending, a);
        setBusy(false);
        if (outcome === 'linked') toast?.success?.(`Added. Continue with ${name} opens this account now.`);
        if (outcome === 'failed') toast?.error?.(`${name} wasn't added`, `Your account still opens with ${link.signedInWith}, as before.`);
        onDone();
    };
    return (
        <div data-testid="signin-link-consent">
            <p className="mb-3 text-sm text-[#3C463E]">
                You're signed in with {link.signedInWith}. Add {name} to this account so either one opens it.
            </p>
            <button type="button" onClick={() => answer('yes')} disabled={busy} data-testid="signin-link-yes"
                className="flex min-h-[44px] w-full items-center justify-center rounded-full px-5 text-[15px] font-semibold text-white disabled:opacity-60"
                style={{ background: GREEN }}>
                {busy ? 'Adding...' : `Yes, add ${name}`}
            </button>
            <div className="mt-1.5 text-center">
                <button type="button" onClick={() => answer('no')} disabled={busy} data-testid="signin-link-no"
                    className="min-h-[44px] text-sm font-semibold underline underline-offset-4" style={{ color: GREEN }}>
                    No thanks
                </button>
            </div>
        </div>
    );
}

function GoogleButton({ onClick, big }) {
    return (
        <button type="button" onClick={onClick} data-testid="signin-sheet-google"
            className={`flex min-h-[44px] w-full items-center justify-center gap-2 rounded-full border-[1.5px] px-5 text-[15px] font-semibold ${big ? 'text-white' : 'bg-white text-[#1F2A22]'}`}
            style={big ? { background: GREEN, borderColor: GREEN } : { borderColor: '#CFCBBE' }}>
            <span aria-hidden="true" className="grid h-5 w-5 place-items-center rounded-full bg-white text-xs font-bold text-[#4285F4]">G</span>
            Continue with Google
        </button>
    );
}

export default function SignInSheet() {
    const sheet = useSyncExternalStore(subscribeSignInSheet, getSignInSheet, getSignInSheet);
    // 'start' | 'email' | 'sent' | 'code_dead'
    const [step, setStep] = useState('start');
    const [sentTo, setSentTo] = useState('');
    const [deadCopy, setDeadCopy] = useState('');
    const panelRef = useRef(null);
    const returnFocus = useRef(null);
    // The student's own close, counted with the step it happened at (#5084).
    const dismissRef = useRef(() => dismissSignInSheet('start'));
    dismissRef.current = () => {
        // Closing the consent is a no (#5154): counted, and the credential dropped.
        if (sheet?.mode === 'link') { answerLink(sheet.link.user, sheet.link.pending, 'no'); closeSignInSheet(); return; }
        dismissSignInSheet(step === 'sent' || step === 'code_dead' ? 'sent' : step === 'email' ? 'email' : 'start');
    };

    useEffect(() => {
        if (!sheet) return undefined;
        returnFocus.current = document.activeElement;
        setStep(sheet.mode === 'in_app' || sheet.fieldFirst ? 'email' : 'start');
        setSentTo('');
        const onKey = (e) => { if (e.key === 'Escape') dismissRef.current(); };
        window.addEventListener('keydown', onKey);
        // With the field first (#5084) the panel takes focus, not the field:
        // the keyboard would rise over the Google button before the student
        // chose, and a screen reader starts at the title.
        const t = setTimeout(() => {
            if (sheet.fieldFirst) { panelRef.current?.focus(); return; }
            const first = panelRef.current?.querySelector('input, button:not([data-close])');
            first?.focus();
        }, 0);
        return () => {
            window.removeEventListener('keydown', onKey);
            clearTimeout(t);
            try { returnFocus.current?.focus?.(); } catch { /* the opener is gone */ }
        };
    }, [sheet]);

    if (!sheet) return null;
    const { mode, entry, app, onGoogle, onProvider, dest } = sheet;
    const providers = sheet.providers || ['google'];
    const more = moreThanGoogle(providers);
    const flow = mode === 'in_app' ? 'in_app_browser' : mode === 'back' ? 'back' : 'sheet';
    // A provider tapped: the caller's own sign-in, straight from the click so
    // a popup keeps its gesture. `position`: its place among the buttons.
    const start = (id, position) => {
        closeSignInSheet();
        if (onProvider) onProvider(id, position);
        else if (id === 'google') onGoogle?.();
    };
    const google = () => start('google', more ? providers.indexOf('google') + 1 : undefined);
    const retryName = providerName(sheet.retry);
    const sent = (email) => { markSheetSent(sheet); setSentTo(email); setStep('sent'); };
    const beforeSend = sheet.answer ? () => awaitSheetAnswer(sheet) : null;
    const dismiss = () => dismissRef.current();

    // Keep Tab inside the sheet while it is open.
    const trap = (e) => {
        if (e.key !== 'Tab' || !panelRef.current) return;
        const items = [...panelRef.current.querySelectorAll('button:not([disabled]), input, a[href]')];
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };

    const title = mode === 'back' ? 'Not signed in yet'
        : mode === 'in_app' ? ((sheet.everyMode && sheet.title) || 'Start free with your email')
            : mode === 'exists' ? 'This email already has a Stratia account'
                : mode === 'link' ? `Also sign in with ${providerName(sheet.link?.pending?.provider)} next time?`
                    : (sheet.title || 'Start free');
    // The quiet email link, or its field once tapped (the 'choose' layout).
    const emailLink = (
        step === 'email' ? (
            <div className="mt-4">
                <EmailLinkForm entry={entry} flow={flow} onSent={sent} autoFocus dest={dest} />
            </div>
        ) : (
            <div className="mt-1.5 text-center">
                <button type="button" onClick={() => setStep('email')} data-testid="signin-sheet-use-email"
                    className="min-h-[44px] text-sm font-semibold underline underline-offset-4" style={{ color: GREEN }}>
                    or use your email
                </button>
            </div>
        )
    );
    // With the field first (#5084) nothing raises the keyboard on its own;
    // the student taps the field.
    const fieldFocus = !sheet.fieldFirst;

    return (
        <div className="fixed inset-0 z-[110] flex items-end justify-center sm:items-center" data-testid="signin-sheet" data-mode={mode}>
            <button type="button" aria-label="Close" tabIndex={-1} data-close
                className="absolute inset-0 h-full w-full cursor-default bg-[#0E1410]/45" onClick={dismiss} />
            <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby="signin-sheet-title" onKeyDown={trap}
                tabIndex={-1}
                className="relative max-h-[100dvh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-white px-5 pb-6 pt-4 shadow-2xl focus:outline-none sm:rounded-2xl sm:px-6">
                <div className="mx-auto mb-3 h-1 w-9 rounded bg-[#D8D5CA] sm:hidden" aria-hidden="true" />
                <button type="button" onClick={dismiss} aria-label="Close sign-in" data-close
                    className="absolute right-2 top-2 grid h-11 w-11 place-items-center rounded-full text-[#5D6A60] hover:bg-[#F3F1EA]">
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                </button>
                <h2 id="signin-sheet-title" className="mb-1.5 pr-10 font-serif text-xl font-bold leading-tight" style={{ color: GREEN }}>
                    {step === 'sent' ? 'Check your email' : step === 'code_dead' ? "That code can't be used" : title}
                </h2>

                {step === 'sent' ? (
                    <CheckYourEmail key={sentTo} email={sentTo} entry={entry} flow={flow} inApp={mode === 'in_app'}
                        onDifferent={() => setStep('email')}
                        onCodeDead={(copy) => { setDeadCopy(copy); setStep('code_dead'); }} />
                ) : step === 'code_dead' ? (
                    <div data-testid="signin-code-dead">
                        <p role="alert" className="mb-3 text-sm text-[#4A544C]">{deadCopy}</p>
                        <EmailLinkForm entry={entry} flow={flow} initialEmail={sentTo} buttonLabel="Email me a new code"
                            onSent={(email) => { setDeadCopy(''); sent(email); }} />
                        <button type="button" onClick={() => setStep('email')}
                            className="mt-1 min-h-[44px] text-sm font-semibold underline underline-offset-4" style={{ color: GREEN }}>
                            Use a different email
                        </button>
                    </div>
                ) : mode === 'link' ? (
                    <LinkConsent link={sheet.link} onDone={closeSignInSheet} />
                ) : mode === 'in_app' && providers.length ? (
                    // An app whose own way in works here (#5154: Facebook
                    // inside Facebook's browser): the email first, then it.
                    <>
                        {sheet.everyMode && sheet.lead && <p data-testid="signin-sheet-lead" className="mb-2 text-sm font-semibold text-[#3C463E]">{sheet.lead}</p>}
                        <p className="mb-3 text-sm text-[#3C463E]">
                            Google doesn't allow sign-in inside {app || 'this app'}. Use your email, or continue with {providers.map(providerName).join(' or ')}.
                        </p>
                        <EmailLinkForm entry={entry} flow={flow} onSent={sent} dest={dest} beforeSend={beforeSend} describedBy="signin-no-password" />
                        <p id="signin-no-password" className="mt-2 text-xs text-[#5D6A60]">No password. Tap the link in the email and you're in.</p>
                        <OrRule />
                        <ProviderButtons ids={providers} onPick={start} />
                    </>
                ) : mode === 'exists' ? (
                    <>
                        <SheetAlert>Sign in the way you did before, and we'll add {retryName} so either works next time.</SheetAlert>
                        <EmailLinkForm entry={entry} flow={flow} onSent={sent} dest={dest} initialEmail={sheet.email || ''} />
                        {providers.length > 0 && (
                            <>
                                <OrRule />
                                <ProviderButtons ids={providers} onPick={start} />
                            </>
                        )}
                    </>
                ) : mode === 'refused' ? (
                    <>
                        <SheetAlert>{sheet.notice}</SheetAlert>
                        <ProviderButtons ids={providers} onPick={start} />
                        {emailLink}
                    </>
                ) : mode === 'in_app' ? (
                    <>
                        {sheet.everyMode && sheet.lead && <p data-testid="signin-sheet-lead" className="mb-2 text-sm font-semibold text-[#3C463E]">{sheet.lead}</p>}
                        <p className="mb-3 text-sm text-[#3C463E]">
                            Google doesn't allow sign-in inside {app || 'this app'}. We'll email you a link instead, no password.
                        </p>
                        <EmailLinkForm entry={entry} flow={flow} onSent={sent} dest={dest} beforeSend={beforeSend} />
                        <p className="mt-3 text-xs text-[#5D6A60]">
                            Prefer Google? Tap <span className="rounded bg-[#111] px-1.5 font-bold tracking-wider text-white" aria-label="the three dots">•••</span> at
                            the top, choose <b>Open in browser</b>, then Start free there.
                        </p>
                    </>
                ) : mode === 'back' ? (
                    <>
                        {sheet.everyMode && sheet.lead && <p data-testid="signin-sheet-lead" className="mb-2 text-sm font-semibold text-[#3C463E]">{sheet.lead}</p>}
                        <p className="mb-3 text-sm text-[#3C463E]">
                            {sheet.retry && sheet.retry !== 'google'
                                ? `${retryName} sign-in didn't finish. We can email you a one-time sign-in link instead, no password.`
                                : <>Google sign-in didn't finish. We can email you a one-time sign-in link instead, no
                                    password. If your school blocks Google, a personal or parent email works.</>}
                        </p>
                        {step === 'email' ? (
                            <EmailLinkForm entry={entry} flow={flow} onSent={sent} autoFocus={fieldFocus} dest={dest} />
                        ) : (
                            <button type="button" onClick={() => setStep('email')} data-testid="signin-sheet-use-email"
                                className="flex min-h-[44px] w-full items-center justify-center rounded-full px-5 text-[15px] font-semibold text-white"
                                style={{ background: GREEN }}>
                                Use an email instead
                            </button>
                        )}
                        <div className="mt-1.5 text-center">
                            <button type="button" onClick={() => start(sheet.retry || 'google')} data-testid="signin-sheet-retry"
                                className="min-h-[44px] text-sm font-semibold underline underline-offset-4" style={{ color: GREEN }}>
                                Try {retryName} again
                            </button>
                        </div>
                    </>
                ) : mode === 'email_first' ? (
                    <>
                        <p className="mb-3 text-sm text-[#3C463E]">{sheet.lead || 'Save your list, your reads and your plan.'}</p>
                        <EmailLinkForm entry={entry} flow={flow} onSent={sent} dest={dest} describedBy="signin-no-password" />
                        <p id="signin-no-password" className="mt-2 text-xs text-[#5D6A60]">No password. Tap the link in the email and you're in.</p>
                        <OrRule />
                        {more ? <ProviderButtons ids={providers} onPick={start} /> : <GoogleButton onClick={google} />}
                    </>
                ) : more ? (
                    // Variant A (#5154): the providers in the platform's order,
                    // then the quiet email link, then the school note.
                    <>
                        <p className="mb-3 text-sm text-[#3C463E]">{sheet.lead || 'Save your list, your reads and your plan.'}</p>
                        <ProviderButtons ids={providers} onPick={start} />
                        {emailLink}
                        <p className="mt-3 rounded-xl bg-[#F6F4EC] px-3 py-2 text-xs text-[#4A544C]">
                            School account not working? Your school may block outside apps. Use a personal or parent email instead.
                        </p>
                    </>
                ) : (
                    <>
                        <p className="mb-3 text-sm text-[#3C463E]">{sheet.lead || 'Save your list, your reads and your plan.'}</p>
                        <GoogleButton onClick={google} big />
                        {step === 'email' ? (
                            <div className="mt-4">
                                <EmailLinkForm entry={entry} flow={flow} onSent={sent} autoFocus dest={dest} />
                            </div>
                        ) : (
                            <div className="mt-1.5 text-center">
                                <button type="button" onClick={() => setStep('email')} data-testid="signin-sheet-use-email"
                                    className="min-h-[44px] text-sm font-semibold underline underline-offset-4" style={{ color: GREEN }}>
                                    or use your email
                                </button>
                            </div>
                        )}
                        <p className="mt-3 rounded-xl bg-[#F6F4EC] px-3 py-2 text-xs text-[#4A544C]">
                            School Google account not working? Your school may block outside apps. Use a personal or parent email instead.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
